'use client';

import { stripHtml } from '@/lib/homepage-mappers';
import type { ProcessPreFooterData } from '@/types/process';

const BRAND_RED = '#EC1C24';

interface ProcessEndSectionProps {
  preFooter?: ProcessPreFooterData | null;
}

function isAccentColor(color: string | null | undefined): boolean {
  if (!color) return false;
  const normalized = color.trim().toUpperCase();
  return normalized === '#EC1C24' || normalized === '#ED1C22' || normalized === '#E31E24';
}

export default function ProcessEndSection({ preFooter }: ProcessEndSectionProps) {
  if (!preFooter) return null;

  const eyebrow = preFooter.eyebrowHeading?.trim();
  const description = preFooter.descriptionHtml?.trim() ? stripHtml(preFooter.descriptionHtml) : '';
  const headingParts = preFooter.heading ?? [];
  const bgIconUrl = preFooter.bgIconUrl;

  if (!eyebrow && !description && headingParts.length === 0) return null;

  return (
    <section
      data-section-blur="false"
      className="relative overflow-hidden px-6 pt-14 pb-10 text-center sm:px-10 sm:pt-16 sm:pb-12 md:px-12 md:pt-10 md:pb-6"
      style={{ background: '#111111' }}
    >
      <div
        className="absolute left-1/2 top-0 h-[3px] w-16 -translate-x-1/2 rounded-b-[3px]"
        style={{ background: BRAND_RED }}
      />

      {bgIconUrl ? (
        <div
          className="pointer-events-none absolute bottom-0 right-0 z-0 h-[clamp(180px,24vw,200px)] w-[clamp(300px,40vw,360px)] bg-contain bg-right-bottom bg-no-repeat opacity-[0.1] sm:opacity-[0.12] md:opacity-[0.14]"
          style={{ backgroundImage: `url('${bgIconUrl}')` }}
          aria-hidden
        />
      ) : null}

      {eyebrow ? (
        <p
          className="relative z-[1] mb-4 text-[8.5px] font-semibold uppercase tracking-[0.3em] sm:mb-5 sm:text-[9.5px] sm:tracking-[0.35em]"
          style={{ color: 'rgba(255,255,255,1)' }}
        >
          {eyebrow}
        </p>
      ) : null}

      {headingParts.length > 0 ? (
        <h2
          className="relative z-[1] mb-4 text-[clamp(1.35rem,5vw,2.75rem)] font-bold leading-[1.08] tracking-tight sm:mb-5 sm:leading-[1.05]"
          style={{ color: '#ffffff' }}
        >
          {headingParts.map((part, index) => {
            const accent = isAccentColor(part.textColor);
            const color = accent ? BRAND_RED : part.textColor || '#ffffff';

            if (index === 0 && headingParts.length > 1) {
              return (
                <span key={part.id ?? index}>
                  <span style={{ color }}>{part.text}</span>
                  <br />
                </span>
              );
            }

            return (
              <span key={part.id ?? index} style={{ color }}>
                {part.text}
              </span>
            );
          })}
        </h2>
      ) : null}

      {description ? (
        <p
          className="relative z-[1] mx-auto mb-8 max-w-[440px] px-2 text-[13px] font-light leading-[1.8] sm:mb-10 sm:px-0 sm:text-sm sm:leading-[1.85]"
          style={{ color: 'rgba(255,255,255,1)' }}
        >
          {description}
        </p>
      ) : null}
    </section>
  );
}
